import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Loads recently viewed products through Shopify Search
 * and Shopify's Section Rendering API.
 */
class RecentlyViewedProductsGrid extends HTMLElement {
  /** @type {boolean} */
  #isLoading = false;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {string} */
  #lastProductIdsKey = '';

  connectedCallback() {
    this.#initialize();

    window.addEventListener('pageshow', this.#handlePageShow);
    window.addEventListener('storage', this.#handleStorageChange);
    document.addEventListener(
      'recently-viewed-products:updated',
      this.#handleRecentlyViewedUpdate
    );
  }

  disconnectedCallback() {
    this.#abortController?.abort();

    window.removeEventListener('pageshow', this.#handlePageShow);
    window.removeEventListener('storage', this.#handleStorageChange);
    document.removeEventListener(
      'recently-viewed-products:updated',
      this.#handleRecentlyViewedUpdate
    );
  }

  /**
   * Waits until the other page module scripts, including scripts.liquid,
   * have had a chance to add the current product to localStorage.
   */
  async #initialize() {
    if (document.readyState === 'loading') {
      await new Promise((resolve) => {
        document.addEventListener('DOMContentLoaded', resolve, {
          once: true
        });
      });
    }

    await new Promise((resolve) => {
      window.setTimeout(resolve, 0);
    });

    this.loadProducts();
  }

  /**
   * Reload when navigating back through the browser cache.
   */
  #handlePageShow = () => {
    this.loadProducts({ force: true });
  };

  /**
   * Reload when localStorage is changed in another browser tab.
   *
   * @param {StorageEvent} event
   */
  #handleStorageChange = (event) => {
    if (event.key !== 'viewedProducts') return;

    this.loadProducts({ force: true });
  };

  /**
   * Reload after the current product is added to the history.
   */
  #handleRecentlyViewedUpdate = () => {
    this.loadProducts({ force: true });
  };

  /**
   * Loads recently viewed products.
   *
   * @param {{ force?: boolean }} options
   * @returns {Promise<void>}
   */
  async loadProducts({ force = false } = {}) {
    const sectionId = this.dataset.sectionId;

    if (!sectionId) {
      this.#hideSection();
      return;
    }

    const productIds = this.#getProductIds();
    const productIdsKey = productIds.join(',');

    if (
      !force &&
      productIdsKey === this.#lastProductIdsKey &&
      this.dataset.loaded === 'true'
    ) {
      return;
    }

    this.#lastProductIdsKey = productIdsKey;

    if (productIds.length === 0) {
      this.#hideSection();
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#isLoading = true;

    const url = new URL(
      Theme.routes.search_url,
      window.location.origin
    );

    url.searchParams.set(
      'q',
      productIds
        .map((productId) => `id:${productId}`)
        .join(' OR ')
    );

    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url.toString(), {
        signal: this.#abortController.signal,
        headers: {
          Accept: 'text/html'
        }
      });

      if (!response.ok) {
        throw new Error(
          `Recently viewed request failed with status ${response.status}`
        );
      }

      const markup = await response.text();

      if (!markup.trim()) {
        this.#hideSection();
        return;
      }

      const parsedDocument = new DOMParser().parseFromString(
        markup,
        'text/html'
      );

      const fetchedElement = parsedDocument.querySelector(
        'recently-viewed-products-grid[data-rendered="true"]'
      );

      if (
        !(fetchedElement instanceof HTMLElement) ||
        !fetchedElement.querySelector('.product-grid__item')
      ) {
        this.#hideSection();
        return;
      }

      this.innerHTML = fetchedElement.innerHTML;

      this.dataset.loaded = 'true';
      this.dataset.rendered = 'true';
      this.hidden = false;

      this.closest('.shopify-section')?.removeAttribute('hidden');

      this.#isLoading = false;
      this.#abortController = null;

      document.dispatchEvent(
        new CustomEvent('recently-viewed-products:loaded', {
          detail: {
            sectionId,
            productIds
          }
        })
      );
    } catch (error) {
      if (
        error instanceof DOMException &&
        error.name === 'AbortError'
      ) {
        return;
      }

      console.error(
        'Recently viewed products could not be loaded:',
        error
      );

      this.#hideSection();
    }
  }

  /**
   * Returns the stored recently viewed product IDs.
   *
   * @returns {string[]}
   */
  #getProductIds() {
    const configuredLimit = Number.parseInt(
      this.dataset.productsCount || '8',
      10
    );

    const limit =
      Number.isFinite(configuredLimit) && configuredLimit > 0
        ? Math.min(configuredLimit, 8)
        : 8;

    const excludeId = this.dataset.excludeId
      ? String(this.dataset.excludeId)
      : null;

    const storedProducts = RecentlyViewed.getProducts();

    if (!Array.isArray(storedProducts)) return [];

    return storedProducts
      .map((productId) => String(productId).trim())
      .filter(Boolean)
      .filter((productId, index, products) => {
        return products.indexOf(productId) === index;
      })
      .filter((productId) => {
        return !excludeId || productId !== excludeId;
      })
      .slice(0, limit);
  }

  /**
   * Hides the empty Shopify section.
   */
  #hideSection() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#isLoading = false;

    this.dataset.loaded = 'true';
    this.dataset.rendered = 'false';
    this.hidden = true;
    this.replaceChildren();

    this.closest('.shopify-section')?.setAttribute('hidden', '');
  }
}

if (!customElements.get('recently-viewed-products-grid')) {
  customElements.define(
    'recently-viewed-products-grid',
    RecentlyViewedProductsGrid
  );
}
